import type { Bundle, CareTeam, Patient } from 'fhir/r4';
import * as fhirpath from 'fhirpath';
import { useEffect, useMemo } from 'react';
import React from 'react';
import { Loader, Message, Card } from 'semantic-ui-react';
import useSWR from 'swr';
import type { PatientEntry } from 'types';

import { useAuthContext } from '../../../../contexts/AuthContext';
import FHIRFetcher from '../../../../data/FHIRFetcher';
import type { ApiFetchError } from '../../../../services/api-client';
import MemberCard from '../card';

import {
  normalizeSearchString,
  buildSearchParam,
  commonSWROptions,
  type PatientSearchResult,
} from './members-list-utils';

interface MyMembersListProps {
  payer: string;
  searchString: string;
  selectedEligibilityGroup?: string | null;
  refresh: boolean;
  setRefresh: (value: boolean) => void;
  onPatientSelect: (patient: Patient) => void;
}

function MyMembersList(props: MyMembersListProps) {
  const authInfo = useAuthContext();
  // Comma-separated patient ids built from the care-team fetch; null until that completes.
  const [myPatientIds, setMyPatientIds] = React.useState<string | null>(null);
  const [patientGroups, setPatientGroups] = React.useState<PatientEntry[]>([]);

  // State variabel with array of  PatientIds..ths will be boiund to the SWR query that gets My pateimts
  const handlePatientSelect = (selectedPatient: Patient) => {
    props.onPatientSelect(selectedPatient);
  };

  const normalizedSearchString = useMemo(() => normalizeSearchString(props.searchString), [props.searchString]);

  const patientOptions = useMemo(
    () => ({
      ...commonSWROptions,
      onSuccess: (data: PatientSearchResult) => {
        const tempPatient: PatientEntry[] = [];
        //TODO: move this to fhir-proxy later
        //filter out the patient data that doesn't meet the criteria
        if (data && data?.entry?.length > 0) {
          for (let i = 0; i < data?.entry?.length; i++) {
            // In range by the loop bound; the assertion satisfies
            // `noUncheckedIndexedAccess` without touching the optional chain.
            const patient = data?.entry[i] as PatientEntry;

            const patientEligibilityGroups = fhirpath.evaluate(
              patient,
              "resource.identifier.where(system = 'https://projectwell.io/fhir/identifiers/eligibility-group').value"
            );
            if (props?.selectedEligibilityGroup && patientEligibilityGroups?.includes(props.selectedEligibilityGroup)) {
              tempPatient.push(patient);
            } else if (!props?.selectedEligibilityGroup) {
              tempPatient.push(patient);
            }
          }
        }
        setPatientGroups(tempPatient);
      },
    }),
    [props?.selectedEligibilityGroup]
  );

  const careTeamOptions = useMemo(
    () => ({
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      onSuccess: (data: Bundle | undefined, _key: unknown, _config: unknown) => {
        //build the PatientIds and set myPatientIds to trigger the patients fetch
        let patientIds = '';
        if (data && data.entry && data.entry.length > 0) {
          let idx = 0;
          data.entry.forEach((entry) => {
            // SUSPECTED DEFECT: `CareTeam.subject` is `0..1` and `Reference.reference`
            // is `0..1`, so a CareTeam without a subject throws
            // "Cannot read properties of undefined (reading 'reference')" here and
            // aborts the whole member list. The `!`s record the two unguarded hops;
            // guarding them would silently drop such a CareTeam instead, which is a
            // different behaviour, not the current one.
            const pateitnRef = (entry.resource as CareTeam).subject!.reference!;
            const patientId = pateitnRef.substring(pateitnRef.indexOf('/') + 1);
            patientIds += (idx > 0 ? ',' : '') + patientId;
            idx++;
          });
          setMyPatientIds(patientIds); //this list is used in the get patinets SWR call below
        }
      },
    }),
    []
  );

  //get all the careTeams that the logged in practitioner is associated with . Max Limit 50
  const careTeamQuery = useMemo(() => {
    if (props.payer === 'ALL') {
      return `/CareTeam?participant=${authInfo.practitionerId}&_count=50`;
    } else {
      return `/CareTeam?participant=${authInfo.practitionerId}&identifier=${props.payer}&_count=50`;
    }
  }, [props.payer, authInfo.practitionerId]);

  //standard patient query
  const patientQuery = useMemo(() => {
    const searchParam = buildSearchParam(normalizedSearchString);

    if (props.payer === 'ALL') {
      return `/Patient/?identifier:not=INACTIVE${searchParam}`;
    } else {
      return `/Patient/?identifier:not=INACTIVE&organization=${props.payer}${searchParam}`;
    }
  }, [props.payer, normalizedSearchString]);

  const {
    data: _careTeamData,
    error: _careteamError,
    isValidating: careTeamDataLoading,
  } = useSWR<Bundle | undefined, ApiFetchError>(
    [careTeamQuery, authInfo.token],
    FHIRFetcher,
    careTeamOptions
  );

  //get patients that the loggedIn user/practitioner is part of the careteam
  //Note: initially myPatientIds will be null and once the above Careteam fetch is complete that myPatientIds list is built
  const {
    data: _patientData,
    error: patientError,
    isValidating: patientsDataLoading,
    mutate: patientsDataMutate,
  } = useSWR<PatientSearchResult, ApiFetchError>(
    [myPatientIds ? patientQuery + '&_id=' + myPatientIds : null, authInfo.token],
    FHIRFetcher,
    patientOptions
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the view stale
    patientsDataMutate();
  }, [props?.selectedEligibilityGroup, patientsDataMutate]);

  useEffect(() => {
    if (props.refresh === true) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the view stale
      patientsDataMutate();
      props.setRefresh(false);
    }
  }, [props.refresh, patientsDataMutate, props.setRefresh]);

  const isLoading = patientsDataLoading || careTeamDataLoading;

  return (
    <div>
      <p />

      {isLoading && <Loader active inline="centered" content="Loading members..." />}

      {patientGroups && patientGroups.length < 1 && !isLoading && (
        <Message warning header="No members found!" content="Please refine your search." />
      )}

      {!isLoading && patientGroups && patientGroups.length > 0 && (
        <Card.Group>
          {patientGroups.map((patientEntry) => {
            return (
              <MemberCard
                key={patientEntry.resource.id}
                patient={patientEntry.resource}
                onPatientSelect={handlePatientSelect}
              />
            );
          })}
        </Card.Group>
      )}

      {patientError && (
        <Message negative>
          <Message.Header>Oops!. Something went wrong.. </Message.Header>
          {/* SUSPECTED DEFECT: renders empty on the string-rejection branch. See `ApiFetchError`. */}
          <p>{(patientError as { message?: string }).message}</p>
        </Message>
      )}
    </div>
  );
}

export default MyMembersList;
